import { useEffect, useCallback, useState } from "react";

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

interface UseScrollAwareDropdownProps {
  elementId: string;
  isOpen: boolean;
  dropdownHeight?: number;
}

export function useScrollAwareDropdown({ elementId, isOpen, dropdownHeight = 300 }: UseScrollAwareDropdownProps) {
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });
  const [isVisible, setIsVisible] = useState(true);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    const inputElement = document.getElementById(elementId);
    if (inputElement && isOpen) {
      const inputRect = inputElement.getBoundingClientRect();

      // Find the scrollable container
      let scrollableContainer: Element | null = null;
      let element = inputElement.parentElement;

      while (element) {
        const styles = window.getComputedStyle(element);
        if (
          styles.overflow === "auto" ||
          styles.overflow === "scroll" ||
          styles.overflowY === "auto" ||
          styles.overflowY === "scroll"
        ) {
          scrollableContainer = element;
          break;
        }
        element = element.parentElement;
      }

      // Calculate the dropdown position
      let dropdownTop = inputRect.bottom + window.scrollY;

      // If we have a scrollable container, ensure dropdown doesn't go beyond it
      if (scrollableContainer) {
        const containerRect = scrollableContainer.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerBottom = containerRect.bottom;

        // Check if input is visible within the container
        if (inputRect.bottom < containerTop || inputRect.top > containerBottom) {
          // Input is scrolled out of view, hide dropdown
          setIsVisible(false);
          return;
        }

        // Input is at least partially visible
        setIsVisible(true);

        // Always try to position below the input
        dropdownTop = inputRect.bottom + window.scrollY;

        // If input is partially hidden at the top, adjust to start at container top
        if (inputRect.bottom < containerTop) {
          // Input is completely above the container, hide dropdown
          setIsVisible(false);
          return;
        } else if (inputRect.top < containerTop && inputRect.bottom > containerTop) {
          // Input is partially visible at top, position dropdown at container top
          dropdownTop = Math.max(containerTop + window.scrollY, inputRect.bottom + window.scrollY);
        }

        // Check if we have enough space to show at least some of the dropdown
        const visibleDropdownSpace = containerBottom - Math.max(inputRect.bottom, containerTop);

        // If less than 50px would be visible, hide the dropdown
        if (visibleDropdownSpace < 50) {
          setIsVisible(false);
          return;
        }

        // If the dropdown would extend beyond the viewport bottom,
        // let the browser handle it with scrolling rather than repositioning
        const viewportBottom = window.innerHeight;
        if (inputRect.bottom + dropdownHeight > viewportBottom) {
          // Just keep it below the input and let natural scrolling handle it
          // This prevents weird jumping behavior
        }
      } else {
        setIsVisible(true);
      }

      setDropdownPosition({
        top: dropdownTop,
        left: inputRect.left + window.scrollX,
        width: inputRect.width,
      });
    }
  }, [elementId, isOpen, dropdownHeight]);

  // Set up scroll listeners
  useEffect(() => {
    if (isOpen) {
      // Initial position calculation
      updateDropdownPosition();

      // Update position on scroll with RAF for better performance
      let rafId: number | null = null;
      const handleScroll = () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          updateDropdownPosition();
          rafId = null;
        });
      };

      // Listen to scroll on window and all parent elements
      const scrollableParents: Element[] = [];
      let element = document.getElementById(elementId)?.parentElement;

      while (element) {
        const styles = window.getComputedStyle(element);
        if (
          styles.overflow === "auto" ||
          styles.overflow === "scroll" ||
          styles.overflowY === "auto" ||
          styles.overflowY === "scroll"
        ) {
          scrollableParents.push(element);
          element.addEventListener("scroll", handleScroll, { passive: true });
        }
        element = element.parentElement;
      }

      // Also listen to window scroll
      window.addEventListener("scroll", handleScroll, { passive: true });

      // Cleanup
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener("scroll", handleScroll);
        scrollableParents.forEach((parent) => {
          parent.removeEventListener("scroll", handleScroll);
        });
      };
    }
  }, [isOpen, elementId, updateDropdownPosition]);

  return {
    dropdownPosition,
    isVisible,
    updateDropdownPosition,
  };
}
